<docs>
The toasts of a page, stacked in the corners of the screen the way Zone::Toast stacks them. Each
toast goes to a corner, its own `position` or the Zone::Toast rule when it has none: success,
warning and error top-right; a neutral toast with a description bottom-left; a neutral one-liner
bottom-center. The toasts of one corner are a deck: the newest in front, the two before it peeking
out behind (10 and 20px further from the edge, a little narrower), the rest hidden until their turn.
Only the front toast of a deck counts down; when it closes, the next slides forward and starts its
own. One Reka provider and viewport per corner in use, 20px off both edges; each item is a card with
its own color, shape and size (`Toast.vue`, internal: no story, never used alone).

## Where the toasts come from

Normally from Rails. `layouts/Page.vue`, the layout every Zone page renders in, holds the one
ZoneToastStack of the page and turns the flash of every Inertia response into items: `notice` /
`success` green, `alert` / `error` red, the text as the title, or a whole object with the keys
below. A page never renders a toast or this component itself; it writes the flash in the
controller, or `router.flash('notice', …)` when its mutation answers legacy JSON. The "Flash
messages" guide has the rules and the live demo.

## ToastStackItem

`toasts` is an array of these, oldest first. Only `title` is needed; the stack keys the items itself.

| Key | Type | Default | What it does |
| --- | --- | --- | --- |
| `title` | `string` | required | The message. On its own it is Figma's simple one-liner. |
| `description` | `string` | — | A second line. Switches to the detailed shape: 16px semibold title, boxed at 350px. |
| `color` | `'neutral-dark' \| 'neutral-light' \| 'success' \| 'warning' \| 'error'` | `'neutral-dark'` | Surface, text and the default icon. Same names as Zone::Toast's `$toast.show`. |
| `position` | `'top-left' \| 'top-center' \| 'top-right' \| 'bottom-left' \| 'bottom-center' \| 'bottom-right'` | by the rule above | The corner. Each corner is its own deck. |
| `icon` | Material Symbols name | per `color` | Replaces the colour's icon (`check_circle`, `warning`, `cancel`, `cached`). |
| `showIcon` | `boolean` | `true` | `false` drops the icon or spinner. |
| `loading` | `boolean` | `false` | Figma's spinner shape: the ring replaces the icon. Pair with `dismissible: false, duration: 0`. |
| `duration` | `number` (ms) | `4000` | How long the toast stays once it is in front; `0` keeps it until dismissed. Hovering the deck pauses it. |
| `dismissible` | `boolean` | `true` | `false` hides the × and swallows Escape. |
| `detailsText` | `string` | `"See details"` | Text of the link. |
| `detailsHref` | `string` | — | Renders the "See details" link: inline after a one-liner, on its own row under a description. |
| `detailsTarget` | `string \| null` | `'_blank'` | `null` opens in the same tab (and drops `rel="noopener noreferrer"`). |
| `sensitivity` | `'foreground' \| 'background'` | `'foreground'` | Live-region politeness: assertive for something the user just did, polite for the rest. |

The stack never removes an item itself: it emits `close` with the item when the front toast of a
deck goes (timeout, ×, swipe, Escape), and the owner drops it from the array, which is what moves
the next one forward.

## Notes

- A viewport is a grid with every card of its corner in the same cell, aligned to the corner, in
  DOM order: the newest is last and so paints on top, no z-index games. Cards keep their own sizes.
  In a bottom corner the cards behind peek out above the front one, and the swipe goes toward the
  pinned edge.
- The items are controlled (`modelValue`): a card pushed back keeps the timer Reka started while it
  was in front, which fires once into the void; its real one starts when it is in front again.
- `dismissible: false` on an item hides its × and swallows Escape; swipe belongs to the viewport,
  so it stays on for the whole deck.
- Hovering a deck pauses its front toast's timer.
- The viewports teleport to `#zone-teleport` (carries `.w-tw`); in tests query `document.body`,
  mount with the real Teleport (the setup.ts stub breaks Reka's toast) and give the page that element.
</docs>

<template>
  <ToastProvider
    v-for="(deck, position) in decks"
    :key="position"
    :label="label"
    :swipe-direction="SWIPE_DIRECTIONS[position]"
  >
    <!--
      Target `#zone-teleport` (defined in the app layouts, see application.scss) rather than the
      reka-ui default of `document.body`: it carries `.w-tw`, which Tailwind utilities need, and the
      stacking context every other piece of floating Zone UI uses.
    -->
    <ToastPortal to="#zone-teleport">
      <ToastViewport
        :class="`${VIEWPORT_CLASSES} ${POSITION_CLASSES[position]}`"
        data-testid="toast-stack-viewport"
      />
    </ToastPortal>

    <ZoneToast
      v-for="(card, index) in deck"
      :key="card.key"
      v-bind="card.props"
      model-value
      :class="DECK_CLASSES[side(position)][deck.length - 1 - index] ?? 'invisible'"
      :duration="index === deck.length - 1 ? card.props.duration : 0"
      @update:model-value="(open) => open || onClose(card.item, index, deck.length)"
    />
  </ToastProvider>
</template>

<script lang="ts">
import { computed, toRaw, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { ToastPortal, ToastProvider, ToastViewport } from 'reka-ui';
import ZoneToast, { type ToastColor, type ToastSensitivity } from './Toast.vue';

export type { ToastColor, ToastSensitivity };

export const POSITIONS = [
  'top-left',
  'top-center',
  'top-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
] as const;

export type ToastStackPosition = (typeof POSITIONS)[number];

/** One item of the stack: the card's props plus the corner. */
export interface ToastStackItem {
  title: string;
  description?: string;
  color?: ToastColor;
  position?: ToastStackPosition;
  icon?: string;
  showIcon?: boolean;
  loading?: boolean;
  duration?: number;
  dismissible?: boolean;
  detailsText?: string;
  detailsHref?: string;
  detailsTarget?: string | null;
  sensitivity?: ToastSensitivity;
}

type SwipeDirection = 'up' | 'down' | 'left' | 'right';

type Side = 'top' | 'bottom';

interface Card {
  key: string;
  item: ToastStackItem;
  props: Omit<ToastStackItem, 'position'>;
}

// The v-for key of each item, made here so the owner never has to: one per object, for as long as
// it is in the array (the raw object, so Vue's proxies of it all map to the same key).
const keys = new WeakMap<object, string>();

function keyOf(item: ToastStackItem): string {
  const raw = toRaw(item);

  if (!keys.has(raw)) {
    keys.set(raw, crypto.randomUUID());
  }

  return keys.get(raw)!;
}

// Zone::Toast's "info" queue: these three colours go top-right when the item names no corner.
const SEVERITY_COLORS: ToastColor[] = ['success', 'warning', 'error'];

// One grid cell for every card of a corner, aligned to it; DOM order is paint order. `list-none` /
// `p-0` undo the <ol> Reka renders. z-50, the same as every other teleported Zone overlay.
const VIEWPORT_CLASSES = 'fixed z-50 grid list-none p-0 outline-none [&>li]:[grid-area:1/1]';

// 20px off both edges, matching the Alpine component's `m-5`. Insets rather than a margin so
// `-translate-x-1/2` still centres the two `*-center` positions exactly.
const POSITION_CLASSES: Record<ToastStackPosition, string> = {
  'top-left': 'top-5 left-5 items-start justify-items-start',
  'top-center': 'top-5 left-1/2 -translate-x-1/2 items-start justify-items-center',
  'top-right': 'top-5 right-5 items-start justify-items-end',
  'bottom-left': 'bottom-5 left-5 items-end justify-items-start',
  'bottom-center': 'bottom-5 left-1/2 -translate-x-1/2 items-end justify-items-center',
  'bottom-right': 'bottom-5 right-5 items-end justify-items-end',
};

// Zone::Toast's deck, by how far behind the front a card is: 10 and 20px further from the pinned
// edge and a little narrower, so they peek out; anything deeper hides until its turn. The card's
// transition animates the change as it moves forward.
const DECK_CLASSES: Record<Side, string[]> = {
  top: ['', 'translate-y-[10px] scale-x-95', 'translate-y-[20px] scale-x-90'],
  bottom: ['', '-translate-y-[10px] scale-x-95', '-translate-y-[20px] scale-x-90'],
};

// Swipe the toast toward the edge it is pinned to.
const SWIPE_DIRECTIONS: Record<ToastStackPosition, SwipeDirection> = {
  'top-left': 'left',
  'top-center': 'up',
  'top-right': 'right',
  'bottom-left': 'left',
  'bottom-center': 'down',
  'bottom-right': 'right',
};

const side = (position: ToastStackPosition): Side => (position.startsWith('top') ? 'top' : 'bottom');

/**
 * Zone::Toast's default-position rules, kept so a flash written for the Alpine component lands in
 * the same corner: the three severity colours go top-right, a neutral toast with a description
 * bottom-left, and a bare neutral one bottom-centre.
 */
function cornerOf(item: ToastStackItem): ToastStackPosition {
  if (item.position) {
    return item.position;
  }

  if (SEVERITY_COLORS.includes(item.color ?? 'neutral-dark')) {
    return 'top-right';
  }

  return item.description ? 'bottom-left' : 'bottom-center';
}
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneToastStack' });

const { t } = useI18n();

const props = defineProps({
  /**
   * The toasts, oldest first, one object per toast: `title`, then `description`, `color`,
   * `position`, `icon`, `showIcon`, `loading`, `duration`, `dismissible`, `detailsText`,
   * `detailsHref`, `detailsTarget`, `sensitivity`. The ToastStackItem table above has each one.
   */
  toasts: {
    type: Array as PropType<ToastStackItem[]>,
    default: () => [],
  },
  /** The screen-reader prefix Reka announces ahead of each toast. */
  label: {
    type: String,
    default: null,
  },
});

const emit = defineEmits<{
  /** The front toast of a deck closed (timeout, ×, swipe, Escape): drop this item from `toasts`. */
  close: [toast: ToastStackItem];
}>();

const label = computed<string>(() => props.label ?? t('label'));

// The cards by corner, `position` taken off them: it is the deck's, not the card's.
const decks = computed(() => props.toasts.reduce<Partial<Record<ToastStackPosition, Card[]>>>((groups, item) => {
  const { position, ...rest } = item;

  (groups[cornerOf(item)] ??= []).push({ key: keyOf(item), item, props: rest });

  return groups;
}, {}));

// Only the front card of a deck leaves. A card behind reports the timer that was running when it
// was pushed back.
function onClose(item: ToastStackItem, index: number, size: number) {
  if (index === size - 1) {
    emit('close', item);
  }
}
</script>

<i18n lang="json">
{
  "en": {
    "label": "Notification"
  }
}
</i18n>
